import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiBearerToken } from '@verydia/contracts';
import { loadAuthConfig, type AuthConfig } from './config.ts';
import { beginAuthorization, endSession, readIdTokenClaims, refreshTokens } from './oauth.ts';
import {
  clearTokens,
  getTokens,
  consumeResumeMarker,
  isExpired,
  setTokens,
  type Tokens,
} from './session.ts';

/**
 * The authentication state machine.
 *
 * `resolving` exists so the shell can render nothing protected while the answer
 * is unknown. Without it there is no state to distinguish "not signed in" from
 * "not sure yet", and every guard defaults to showing the page for one frame
 * before redirecting — the protected-content flash (§29).
 */
export type AuthStatus = 'resolving' | 'authenticated' | 'anonymous';

export interface AuthState {
  readonly status: AuthStatus;
  readonly email: string | null;
  readonly displayName: string | null;
  /** Signs in, returning to `returnTo` afterwards. */
  readonly signIn: (returnTo?: string) => void;
  readonly signOut: () => void;
  /**
   * The bearer token for API calls, refreshed first if it is close to expiry.
   *
   * WHICH token is not decided here. `apiBearerToken` in `@verydia/contracts`
   * decides, and the API's verifier is configured from the same constant, so
   * the two ends cannot disagree. They did once, in production, when each side
   * named a token type independently (ADR-016).
   */
  readonly getApiToken: () => Promise<string | null>;
  /**
   * Adopts tokens obtained by the callback route.
   *
   * The callback must hand them over rather than reload, because the tokens live
   * in memory: a reload would discard the ones it just exchanged a single-use
   * code for, and bounce the user back through Cognito to get more.
   */
  readonly completeSignIn: (tokens: Tokens) => void;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Supplies a fixed auth state.
 *
 * Exists for tests, which need to mount the shell as a signed-in operator
 * without a Cognito round trip. Kept next to the real provider rather than in a
 * test helper so the two cannot drift apart in shape.
 */
export function AuthStateProvider({ value, children }: { value: AuthState; children: ReactNode }) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const configRef = useRef<AuthConfig | null>(null);
  if (configRef.current === null) configRef.current = loadAuthConfig(import.meta.env);
  const config = configRef.current;

  const [status, setStatus] = useState<AuthStatus>('resolving');
  const [claims, setClaims] = useState<{ email: string; name?: string } | null>(null);

  const adopt = useCallback((next: Tokens) => {
    setTokens(next);
    const parsed = readIdTokenClaims(next.idToken);
    setClaims(
      parsed === null
        ? null
        : { email: parsed.email, ...(parsed.name ? { name: parsed.name } : {}) },
    );
    setStatus('authenticated');
  }, []);

  const signIn = useCallback(
    (returnTo?: string) => {
      void beginAuthorization(config, {
        returnTo: returnTo ?? `${window.location.pathname}${window.location.search}`,
        silent: false,
      });
    },
    [config],
  );

  const signOut = useCallback(() => {
    clearTokens();
    setClaims(null);
    setStatus('anonymous');
    endSession(config);
  }, [config]);

  /**
   * Serialised: a page that fires six queries at once must not fire six token
   * refreshes, five of which race the first and lose. The in-flight promise is
   * shared so they all await the same renewal.
   */
  const refreshInFlight = useRef<Promise<string | null> | null>(null);

  const getApiToken = useCallback(async (): Promise<string | null> => {
    const current = getTokens();
    if (current === null) return null;
    if (!isExpired(current)) return apiBearerToken(current);
    if (current.refreshToken === null) return null;

    refreshInFlight.current ??= (async () => {
      try {
        const next = await refreshTokens(config, current.refreshToken!);
        adopt(next);
        return apiBearerToken(next);
      } catch {
        // The refresh token is spent or revoked. Drop to anonymous rather than
        // retrying: whatever the user does next will send them through sign-in,
        // which is the only thing that can fix it.
        clearTokens();
        setClaims(null);
        setStatus('anonymous');
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    return refreshInFlight.current;
  }, [adopt, config]);

  // Resolve the initial state exactly once.
  useEffect(() => {
    // The callback route owns the code exchange; racing it here would consume
    // the single-use code twice.
    if (window.location.pathname === '/auth/callback') return;

    const existing = getTokens();
    if (existing !== null && !isExpired(existing)) {
      setStatus('authenticated');
      return;
    }

    // Consumes the marker: one silent attempt per tab, then fall back to our own
    // sign-in page rather than bouncing to Cognito forever (ADR-013).
    if (!consumeResumeMarker()) {
      setStatus('anonymous');
      return;
    }

    // This tab had a session before the reload. Cognito still holds its own
    // session cookie, so `prompt=none` gets a fresh code back without showing
    // the user anything.
    void beginAuthorization(config, {
      returnTo: `${window.location.pathname}${window.location.search}`,
      silent: true,
    });
  }, [config]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      email: claims?.email ?? null,
      displayName: claims?.name ?? null,
      signIn,
      signOut,
      getApiToken,
      completeSignIn: adopt,
    }),
    [status, claims, signIn, signOut, getApiToken, adopt],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}
